package diana.soleil.design.iraniancanadienbazzar.navigation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.navigation.NavBackStackEntry
import androidx.navigation.NavHostController
import androidx.navigation.toRoute
import diana.soleil.design.iraniancanadienbazzar.notifications.NotificationDeepLinks
import kotlinx.browser.window
import org.w3c.dom.events.Event

/**
 * Web history binding — the fix for the SPA back-button trap (audit HIGH). `window.history` mirrors
 * the nav back-stack DEPTH (stored in each history entry's `state.depth`).
 *
 * **Strictly one-way in each direction — this is what makes it loop-free** (the previous version
 * called `history.go()` from the nav collector, so a nav change drove the browser, which fired
 * `popstate`, which popped the nav, which the collector saw again → taps "flash back" and appear
 * dead). Here:
 *  - **browser → nav:** `popstate` (the only popstate source is the real Back/Forward buttons) pops
 *    the nav stack down to the entry's recorded depth. Nothing else ever calls `history.go()`.
 *  - **nav → browser:** a forward nav (`navDepth > browserDepth`) `pushState`s; anything else
 *    (same level, or an in-app Back that shrank the stack) `replaceState`s the current entry. The
 *    collector NEVER navigates the browser, so it can never trigger its own `popstate`.
 *
 * Trade-off: after an *in-app* Back, the browser's Forward button is inert (we don't re-push a
 * specific route) — safe and rare, versus the critical win that the browser Back button no longer
 * exits the app. Business/marketplace/news routes also get a real, shareable, refresh-safe URL.
 */
@Composable
actual fun BindWebNavHistory(navController: NavHostController) {
    DisposableEffect(navController) {
        // Browser Back/Forward: pop the nav stack down to whatever depth the browser landed on.
        // Bounded loop guards against any pathological depth reading.
        val onPopState: (Event) -> Unit = {
            // Floor at 2 (graph root + start destination): a pre-SPA history entry carries no depth
            // state (reads 0) and popping past the start destination leaves a dead blank NavHost.
            val target = maxOf(historyDepth(), MIN_DEPTH)
            var guard = 0
            while (navController.currentBackStack.value.size > target && guard < MAX_POP) {
                if (!navController.popBackStack()) break
                guard++
            }
        }
        window.addEventListener("popstate", onPopState)
        onDispose { window.removeEventListener("popstate", onPopState) }
    }

    LaunchedEffect(navController) {
        // Boot deep-link: turn the initial URL into an in-app route via the same parser tapped
        // App-Links use. The deployed SPA lives under /app/ (dev-run at /), so strip that base first
        // and canonicalize to a host-relative link the parser understands. AppNavHost observes
        // NotificationDeepLinks.pending and navigates on top of the resolved start destination.
        val rel = window.location.pathname.removePrefix(appBase()).trimStart('/')
        if (rel.isNotEmpty()) NotificationDeepLinks.offerUrl("https://mahaleh.ca/$rel")

        // Baseline the current entry's depth so the first comparison has a reference.
        replaceDepth(navController.currentBackStack.value.size, window.location.pathname)

        // Mirror every nav-stack change into browser history — push on forward, replace otherwise.
        // Crucially, NO branch here calls history.go(): browser→nav and nav→browser stay one-way.
        navController.currentBackStackEntryFlow.collect { entry ->
            val navDepth = navController.currentBackStack.value.size
            val browserDepth = historyDepth()
            val url = pathFor(entry)
            if (navDepth > browserDepth) {
                pushDepth(navDepth, url)   // forward in-app nav → new history entry
            } else {
                replaceDepth(navDepth, url) // same level, or in-app Back shrank the stack → relabel
            }
        }
    }
}

/** Upper bound on pops per popstate — a safety net, never hit in normal use. */
private const val MAX_POP = 64

/** Never pop below the graph root + start destination (an empty NavHost renders dead-blank). */
private const val MIN_DEPTH = 2

/**
 * The path prefix the SPA writes into the address bar. On the deployed site it is ALWAYS `/app`,
 * even though the app is also served at `/`: the bare paths (`/event/x`, `/gems/poem/x`, `/item/x`,
 * `/post/x`) belong to the public share landing pages (`shareLanding` Function + Hosting rewrites),
 * so a signed-in member who refreshes on `/event/x` would otherwise land on the marketing page
 * instead of back in the app. Everything under the `/app` prefix always resolves to the SPA. The
 * webpack dev server (the only host with a port) has no `/app` mount, so it stays at the root.
 * (Kotlin nests block comments, so this doc must never spell the prefix with a trailing glob.)
 */
private fun appBase(): String = if (window.location.port.isNotEmpty() && !window.location.pathname.startsWith("/app")) "" else "/app"

/**
 * A shareable, refresh-safe path for the routes that have one; the app root otherwise. Kept under the
 * [appBase] so a refresh hits the "app-glob → app index.html" hosting rewrite (loading the SPA) rather
 * than a static SEO page. Only the business "b" path doubles as an SEO path, but the app-prefixed form
 * is distinct and always serves the app.
 */
private fun pathFor(entry: NavBackStackEntry): String {
    val base = appBase()
    runCatching { entry.toRoute<Route.BusinessDetail>() }.getOrNull()?.let { return "$base/b/${it.businessId}" }
    runCatching { entry.toRoute<Route.MarketplaceItem>() }.getOrNull()?.let { return "$base/item/${it.itemId}" }
    runCatching { entry.toRoute<Route.NewsDetail>() }.getOrNull()?.let { return "$base/news/${it.articleId}" }
    // Gem readers that have a share link keep the same path shape, so copying the browser URL and
    // tapping a shared link land on the same screen.
    runCatching { entry.toRoute<Route.GemsPoemDetail>() }.getOrNull()?.let { return "$base/gems/poem/${it.poemId}" }
    runCatching { entry.toRoute<Route.GemsStoryDetail>() }.getOrNull()?.let { return "$base/gems/story/${it.storyId}" }
    return "$base/"
}

private fun historyDepth(): Int =
    (js("(window.history.state && typeof window.history.state.depth === 'number') ? window.history.state.depth : 0") as Int)

private fun pushDepth(depth: Int, url: String) {
    val state: dynamic = js("({})")
    state.depth = depth
    window.history.pushState(state, "", url)
}

private fun replaceDepth(depth: Int, url: String) {
    val state: dynamic = js("({})")
    state.depth = depth
    window.history.replaceState(state, "", url)
}
